import { create } from 'zustand';
import { orderTaskService } from '../api/orderTaskService';
import { getErrorMessage } from '@/shared/lib/errors';
import { RequestStatus } from '@/shared/model/requestStatus';
import type { OrderTaskData, OrderTaskState } from './types';

const initialState: OrderTaskData = {
    orderTask: null,
    detailsStatus: RequestStatus.Idle,
    detailsError: null,
    bindingStatus: RequestStatus.Idle,
    bindingError: null,
};

let latestRequest = 0;

/** Хранит открытую сделку к заданию и состояние её загрузки */
export const useOrderTasks = create<OrderTaskState>()((set) => ({
    ...initialState,

    bindOrderTask: async (request) => {
        const requestId = latestRequest;

        set({
            bindingStatus: RequestStatus.Loading,
            bindingError: null,
        });

        try {
            const orderTask = await orderTaskService.bind(request);

            if (requestId !== latestRequest) return false;

            set({
                orderTask,
                bindingStatus: RequestStatus.Success,
            });

            return true;
        } catch (error: unknown) {
            if (requestId === latestRequest) {
                set({
                    bindingStatus: RequestStatus.Error,
                    bindingError: getErrorMessage(error, 'Не удалось связать сделку с заказом'),
                });
            }

            return false;
        }
    },

    getByOrderId: async (orderId) => {
        const requestId = ++latestRequest;

        set({
            bindingStatus: RequestStatus.Idle,
            bindingError: null,
            orderTask: null,
            detailsStatus: RequestStatus.Loading,
            detailsError: null
        });

        try {
            const orderTask = await orderTaskService.getByOrderId(orderId);

            if (requestId === latestRequest) {
                set({
                    orderTask,
                    detailsStatus: RequestStatus.Success
                });
            }
        } catch (error: unknown) {
            if (requestId === latestRequest) {
                set({
                    detailsStatus: RequestStatus.Error,
                    detailsError: getErrorMessage(error, 'Не удалось загрузить сделку'),
                });
            }
        }
    },

    getTaskByTaskId: async (taskId) => {
        const requestId = ++latestRequest;

        set({
            bindingStatus: RequestStatus.Idle,
            bindingError: null,
            orderTask: null,
            detailsStatus: RequestStatus.Loading,
            detailsError: null
        });

        try {
            const orderTask = await orderTaskService.getByTaskId(taskId);

            if (requestId === latestRequest) {
                set({
                    orderTask,
                    detailsStatus: RequestStatus.Success
                });
            }
        } catch (error: unknown) {
            if (requestId === latestRequest) {
                set({
                    detailsStatus: RequestStatus.Error,
                    detailsError: getErrorMessage(error, 'Не удалось загрузить сделку'),
                });
            }
        }
    },

    clearOrderTask: () => {
        latestRequest += 1;
        set({ ...initialState });
    },
}));
